import { useCallback, useState } from 'react';
import { t } from 'i18next';
import { useTwilioDevice, TwilioDeviceStatus } from '@/hooks/use-twilio-device/use-twilio-device';
import {
  startConference as startConferenceApi,
  endConference as endConferenceApi,
} from '@/services/webchat-service/webchat.service';

type ConferenceState = {
  conferenceName: string | null;
  callSids: string[];
  mode?: 'translated';
};

const SESSION_KEY = 'webchat_conference';

function getStoredConference(): ConferenceState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeConference(state: ConferenceState) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
}

function clearStoredConference() {
  sessionStorage.removeItem(SESSION_KEY);
}

export const useConferenceCall = (conversationId: number | undefined) => {
  const twilioDevice = useTwilioDevice();
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stored = getStoredConference();
  const isActive = !!stored?.conferenceName || stored?.mode === 'translated';

  const startConference = useCallback(
    async (fromNumberId: number, participantNumbers: string[], translationEnabled?: boolean) => {
      if (!conversationId) return;

      try {
        setIsStarting(true);
        setError(null);

        if (translationEnabled) {
          const result = await startConferenceApi(conversationId, {
            from_number_id: fromNumberId,
            translation_enabled: true,
            visitor_number: participantNumbers[0],
          });

          storeConference({
            conferenceName: null,
            callSids: result.visitor_call_sid ? [result.visitor_call_sid] : [],
            mode: 'translated',
          });

          await twilioDevice.connect(conversationId, '', {
            mode: 'translated',
            conversation_id: String(conversationId),
          });
        } else {
          const result = await startConferenceApi(conversationId, {
            from_number_id: fromNumberId,
            participant_numbers: participantNumbers,
          });

          storeConference({
            conferenceName: result.conference_name ?? null,
            callSids: result.participant_call_sids ?? [],
          });

          await twilioDevice.connect(conversationId, result.conference_name ?? '');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : t('webchat:conference.errors.start');
        setError(message);
      } finally {
        setIsStarting(false);
      }
    },
    [conversationId, twilioDevice]
  );

  const terminateConference = useCallback(async () => {
    const conference = getStoredConference();
    if (!conversationId || !conference) return;

    try {
      setIsEnding(true);
      setError(null);

      twilioDevice.disconnect();

      if (conference.callSids.length > 0) {
        await endConferenceApi(conversationId, { call_sids: conference.callSids });
      }

      clearStoredConference();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('webchat:conference.errors.end');
      setError(message);
    } finally {
      setIsEnding(false);
    }
  }, [conversationId, twilioDevice]);

  const reconnectBrowser = useCallback(async () => {
    const conference = getStoredConference();
    if (!conversationId || !conference) return;

    if (conference.mode === 'translated') {
      await twilioDevice.connect(conversationId, '', {
        mode: 'translated',
        conversation_id: String(conversationId),
      });
    } else if (conference.conferenceName) {
      await twilioDevice.connect(conversationId, conference.conferenceName);
    }
  }, [conversationId, twilioDevice]);

  return {
    isActive,
    isStarting,
    isEnding,
    error,
    conferenceName: stored?.conferenceName ?? null,
    callSids: stored?.callSids ?? [],
    browserStatus: twilioDevice.status as TwilioDeviceStatus,
    isMuted: twilioDevice.isMuted,
    browserError: twilioDevice.error,
    startConference,
    terminateConference,
    reconnectBrowser,
    toggleMute: twilioDevice.toggleMute,
  };
};
